import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramBrakeHorsepower } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.motorBhp.v1';

type Fields = {
  nameplateHP: string;
  nameplateFLA: string;
  measuredAmps: string;
  brakeHP: string;
};
const EMPTY: Fields = { nameplateHP: '', nameplateFLA: '', measuredAmps: '', brakeHP: '' };

/**
 * Motor Brake Horsepower — TAB field estimation method.
 *   BHP = HP × (A / FLA)
 */
const ElectricalMotorCalculationsBrakeHorsepowerScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp } = useUnits();

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 3) {
      setError('Fill any 3 of the 4 fields to solve for the missing one.');
      return;
    }
    if (filled === 4) {
      setError('All 4 values are filled — clear one to solve for it.');
      return;
    }
    const hp = toImp(fields.nameplateHP, 'power');
    const fla = parseFloat(fields.nameplateFLA);
    const amps = parseFloat(fields.measuredAmps);
    const bhp = toImp(fields.brakeHP, 'power');

    if (fields.brakeHP === '') {
      if (!(hp > 0) || !(fla > 0) || !(amps >= 0)) {
        setError('HP and FLA must be > 0; measured amps must be ≥ 0.');
        return;
      }
      setSolvedFor('brakeHP');
      set({ brakeHP: fromImp((hp * amps) / fla, 'power', 3) });
    } else if (fields.measuredAmps === '') {
      if (!(hp > 0) || !(fla > 0) || !(bhp >= 0)) {
        setError('HP and FLA must be > 0; BHP must be ≥ 0.');
        return;
      }
      setSolvedFor('measuredAmps');
      set({ measuredAmps: ((bhp * fla) / hp).toFixed(2) });
    } else if (fields.nameplateFLA === '') {
      if (!(hp > 0) || !(bhp > 0) || !(amps > 0)) {
        setError('HP, BHP and measured amps must all be > 0.');
        return;
      }
      setSolvedFor('nameplateFLA');
      set({ nameplateFLA: ((hp * amps) / bhp).toFixed(2) });
    } else if (fields.nameplateHP === '') {
      if (!(fla > 0) || !(bhp > 0) || !(amps > 0)) {
        setError('FLA, BHP and measured amps must all be > 0.');
        return;
      }
      setSolvedFor('nameplateHP');
      set({ nameplateHP: fromImp((bhp * fla) / amps, 'power', 2) });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.nameplateHP && { label: `Nameplate ${unit('power')}`, value: fields.nameplateHP, unit: unit('power'), emphasis: solvedFor === 'nameplateHP' },
    fields.nameplateFLA && { label: 'Nameplate FLA', value: fields.nameplateFLA, unit: 'A', emphasis: solvedFor === 'nameplateFLA' },
    fields.measuredAmps && { label: 'Measured Amps', value: fields.measuredAmps, unit: 'A', emphasis: solvedFor === 'measuredAmps' },
    fields.brakeHP && { label: `Brake ${unit('power')}`, value: fields.brakeHP, unit: unit('power'), emphasis: solvedFor === 'brakeHP' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Motor Brake Horsepower"
      subtitle="Estimate motor BHP from nameplate data and measured amp draw"
      formula={
        <>
          <FormulaLine>BHP = HP × (A / FLA)</FormulaLine>
          <FormulaNote>
            HP = Nameplate Power ({unit('power')}), FLA = Nameplate Full-Load Amps, A = Measured Running Amps.
            TAB field estimation method — assumes load varies approximately linearly with amp draw.
          </FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: BHP = HP × (A / FLA)"
      diagram={<DiagramBrakeHorsepower />}
      outputSlots={{
        motor_fla: fields.nameplateFLA ? parseFloat(fields.nameplateFLA) : undefined,
        amps: fields.measuredAmps ? parseFloat(fields.measuredAmps) : undefined,
        bhp: fields.brakeHP ? toImp(fields.brakeHP, 'power') : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.motor_fla != null && !fields.nameplateFLA) {
          patch.nameplateFLA = values.motor_fla.toFixed(2);
        }
        if (values.amps != null && !fields.measuredAmps) {
          patch.measuredAmps = values.amps.toFixed(2);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Nameplate ${unit('power')}`}
          value={fields.nameplateHP}
          onChangeText={(v) => set({ nameplateHP: v })}
          rightText={unit('power')}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Nameplate FLA (amps)"
          value={fields.nameplateFLA}
          onChangeText={(v) => set({ nameplateFLA: v })}
          rightText="A"
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Measured Amps"
          value={fields.measuredAmps}
          onChangeText={(v) => set({ measuredAmps: v })}
          rightText="A"
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Brake ${unit('power')} (BHP)`}
          value={fields.brakeHP}
          onChangeText={(v) => set({ brakeHP: v })}
          rightText={unit('power')}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
};

export default ElectricalMotorCalculationsBrakeHorsepowerScreen;
